import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Landmark,
  Search,
  Users,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { BrandedCard, Footer } from "@/components/invoice-layout";
import {
  fetchBillPayment,
  fetchBillPaymentInstitutions,
  payBillByBank,
  type BillPaymentLinkError,
  type PaymentInstitution,
  type PublicBillPaymentResponse,
  type YapilyDisclosure,
} from "@/api/public";
import {
  isBillPaymentMockState,
  mockBillPayment,
  mockBillPaymentInstitutions,
  type BillPaymentMockState,
} from "@/api/fixtures";
import {
  DOLFIN_ACCENT,
  isDarkEnoughForText,
  parseBrandColour,
  pickForeground,
  tintFor,
} from "@/lib/brand";
import { cn } from "@/lib/utils";
import { formatCurrency, formatDate } from "@/lib/format";

const DEFAULT_TITLE = "Pay bill";

// Past this many banks a scrolling list stops being scannable, so a search box appears above it.
const BANK_SEARCH_THRESHOLD = 8;

// After the bank sends the payer back, follow the attempt until it settles. Most settle within seconds; a
// business account waiting on a second approver can take hours, so stop and say the page can be closed.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

// States in which the backend will start a payment (BillPaymentService.PayableStates).
const PAYABLE_STATES = ["Approved", "Scheduled", "PaymentInProgress", "PaymentFailed", "PartiallyPaid"];

type Bill = PublicBillPaymentResponse;

type Stage =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "network-error" }
  | { kind: "voided"; bill: Bill }
  // justPaid: the payer arrived back from their bank (or watched it settle here), not a later visit.
  | { kind: "paid"; bill: Bill; justPaid: boolean }
  | { kind: "unavailable"; bill: Bill; reason: string | null }
  | { kind: "payable"; bill: Bill }
  | { kind: "choosing-bank"; bill: Bill }
  | { kind: "redirecting"; bill: Bill }
  | { kind: "in-progress"; bill: Bill; timedOut: boolean }
  | { kind: "failed"; bill: Bill };

// One place decides what the page shows for a bill, so the first load and every poll agree.
// returnStatus is the bank callback's `status` query param: "authorised" | "failed" | null.
function stageFor(bill: Bill, returnStatus: string | null): Stage {
  if (bill.state === "Voided") return { kind: "voided", bill };
  if (bill.state === "Paid") return { kind: "paid", bill, justPaid: returnStatus !== null };

  const latest = bill.latestPayment?.status;
  // Money may still move: never offer a second payment over the top of this one.
  if (latest === "Authorised" || latest === "Pending") return { kind: "in-progress", bill, timedOut: false };
  if (returnStatus === "failed") return { kind: "failed", bill };
  if (returnStatus === "authorised") {
    if (latest === "Failed" || latest === "Expired") return { kind: "failed", bill };
    return { kind: "in-progress", bill, timedOut: false };
  }

  if (bill.unavailableReason) return { kind: "unavailable", bill, reason: bill.unavailableReason };
  // The link outlives the payable states (e.g. a bill reopened for review): show it, but offer nothing.
  if (!PAYABLE_STATES.includes(bill.state)) return { kind: "unavailable", bill, reason: null };
  if (bill.state === "PaymentFailed") return { kind: "failed", bill };
  return { kind: "payable", bill };
}

function stageForError(error: BillPaymentLinkError): Stage {
  if (error.kind === "network") return { kind: "network-error" };
  if (error.kind === "unknown") console.error("Unexpected bill payment link error", { error });
  return { kind: "invalid" };
}

export function BillPaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  const mockParam = process.env.NODE_ENV !== "production" ? searchParams.get("mock") : null;
  const mockState: BillPaymentMockState | null = isBillPaymentMockState(mockParam) ? mockParam : null;
  // Set by the backend's bank callback: /p/{token}?billPaymentId=<id>&status=authorised|failed.
  const returnStatus = searchParams.get("billPaymentId") ? searchParams.get("status") : null;

  const bill = "bill" in stage ? stage.bill : null;
  const brand = parseBrandColour(bill?.clientPrimaryColour) ?? DOLFIN_ACCENT;

  useEffect(() => {
    if (!bill) {
      document.title = DEFAULT_TITLE;
      return;
    }
    const to = bill.supplierName ? ` to ${bill.supplierName}` : "";
    document.title = `Pay ${formatCurrency(bill.outstanding, bill.currency)}${to}`;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [bill?.outstanding, bill?.currency, bill?.supplierName]);

  useEffect(() => {
    if (!token) {
      setStage({ kind: "invalid" });
      return;
    }
    if (mockState) {
      const fixture = mockBillPayment(mockState);
      if (mockState === "network-error") setStage({ kind: "network-error" });
      else setStage(fixture ? stageFor(fixture, null) : { kind: "invalid" });
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await fetchBillPayment(token);
      if (cancelled) return;
      setStage(result.ok ? stageFor(result.data, returnStatus) : stageForError(result.error));
    })();
    return () => {
      cancelled = true;
    };
  }, [token, mockState, returnStatus, reloadKey]);

  // Poll while a payment is in flight. A failed poll is ignored: the next tick tries again.
  const polling = stage.kind === "in-progress" && !stage.timedOut && !mockState;
  useEffect(() => {
    if (!polling || !token) return;
    const startedAt = Date.now();
    let cancelled = false;
    const timer = setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        clearInterval(timer);
        setStage((s) => (s.kind === "in-progress" ? { ...s, timedOut: true } : s));
        return;
      }
      const result = await fetchBillPayment(token);
      if (cancelled || !result.ok) return;
      // Still in flight comes back as in-progress, which keeps this effect (and its clock) running.
      setStage((s) => (s.kind === "in-progress" ? stageFor(result.data, "authorised") : s));
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [polling, token]);

  const reload = () => {
    setStage({ kind: "loading" });
    setReloadKey((k) => k + 1);
  };

  const pageStyle = {
    backgroundColor: tintFor(brand),
    ["--ring" as string]: brand,
  } as CSSProperties;

  return (
    <div className="flex min-h-screen items-center justify-center p-4 sm:p-6" style={pageStyle}>
      <div className="w-full max-w-md">
        <StageView
          stage={stage}
          token={token ?? ""}
          brand={brand}
          mock={mockState !== null}
          setStage={setStage}
          onReload={reload}
        />
        <Footer note={null} />
      </div>
    </div>
  );
}

function StageView({
  stage,
  token,
  brand,
  mock,
  setStage,
  onReload,
}: {
  stage: Stage;
  token: string;
  brand: string;
  mock: boolean;
  setStage: (stage: Stage) => void;
  onReload: () => void;
}) {
  switch (stage.kind) {
    case "loading":
      return (
        <Card>
          <CardContent className="flex min-h-[260px] items-center justify-center">
            <Spinner />
          </CardContent>
        </Card>
      );
    case "invalid":
      return (
        <MessageCard
          icon={<AlertTriangle className="h-10 w-10 text-muted-foreground" />}
          title="This payment link is no longer valid"
          description="Ask whoever sent it for a new link."
        />
      );
    case "network-error":
      return (
        <MessageCard
          icon={<AlertTriangle className="h-10 w-10 text-muted-foreground" />}
          title="Couldn't load this bill"
          description="Check your connection and try again."
        >
          <Button onClick={onReload}>Retry</Button>
        </MessageCard>
      );
    case "voided":
      return (
        <MessageCard
          brand={brand}
          icon={<XCircle className="h-10 w-10 text-muted-foreground" />}
          title="This bill has been cancelled"
          description="There's nothing to pay."
        >
          <BillReference bill={stage.bill} />
        </MessageCard>
      );
    case "paid":
      return (
        <MessageCard
          brand={brand}
          icon={<CheckCircle2 className="h-10 w-10 text-emerald-500" />}
          title={stage.justPaid ? "Payment sent" : "This bill has already been paid"}
          description={
            stage.justPaid
              ? `Your bank has sent the payment${stage.bill.supplierName ? ` to ${stage.bill.supplierName}` : ""}. You can close this page.`
              : undefined
          }
        >
          <div className="space-y-1">
            <div>{formatCurrency(stage.bill.totalAmount, stage.bill.currency)}</div>
            <BillReference bill={stage.bill} />
          </div>
        </MessageCard>
      );
    case "unavailable": {
      const copy = unavailableCopy(stage.reason, stage.bill.currency);
      return (
        <BillSummaryCard bill={stage.bill} brand={brand}>
          <Notice icon={<AlertTriangle className="h-5 w-5 text-amber-500" />} title={copy.title}>
            {copy.body}
          </Notice>
          <Button variant="outline" className="w-full" onClick={onReload}>
            Check again
          </Button>
        </BillSummaryCard>
      );
    }
    case "payable":
      return (
        <BillSummaryCard bill={stage.bill} brand={brand}>
          <PayByBankButton
            brand={brand}
            label="Pay by bank"
            onClick={() => setStage({ kind: "choosing-bank", bill: stage.bill })}
          />
        </BillSummaryCard>
      );
    case "failed": {
      const expired = stage.bill.latestPayment?.status === "Expired";
      const reason = stage.bill.latestPayment?.errorMessage;
      return (
        <BillSummaryCard bill={stage.bill} brand={brand}>
          <Notice
            icon={<XCircle className="h-5 w-5 text-destructive" />}
            title="Your payment didn't go through"
          >
            <p>
              {expired
                ? "The approval at your bank wasn't finished in time."
                : "Your bank didn't complete the payment."}{" "}
              You can try again with the same bank or a different one.
            </p>
            {reason && !expired ? <p className="pt-1 text-xs">Reason given: {reason}</p> : null}
          </Notice>
          <PayByBankButton
            brand={brand}
            label="Try again"
            onClick={() => setStage({ kind: "choosing-bank", bill: stage.bill })}
          />
        </BillSummaryCard>
      );
    }
    case "choosing-bank":
      return (
        <BillSummaryCard bill={stage.bill} brand={brand} compact>
          <BankPicker
            token={token}
            bill={stage.bill}
            brand={brand}
            mock={mock}
            onBack={() => setStage(stageFor(stage.bill, null))}
            onRedirecting={() => setStage({ kind: "redirecting", bill: stage.bill })}
            onUnavailable={(reason) => setStage({ kind: "unavailable", bill: stage.bill, reason })}
            onInvalid={() => setStage({ kind: "invalid" })}
          />
        </BillSummaryCard>
      );
    case "redirecting":
      return (
        <BillSummaryCard bill={stage.bill} brand={brand} compact>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <Spinner className="size-6" />
            <div className="space-y-1">
              <p className="font-medium">Taking you to your bank</p>
              <p className="text-sm text-muted-foreground">
                Approve the payment there and you'll come straight back here.
              </p>
            </div>
          </div>
        </BillSummaryCard>
      );
    case "in-progress":
      return (
        <BillSummaryCard bill={stage.bill} brand={brand} compact>
          <InProgress
            bill={stage.bill}
            timedOut={stage.timedOut}
            onCheckAgain={() => setStage({ ...stage, timedOut: false })}
          />
        </BillSummaryCard>
      );
  }
}

// Payer-facing copy per unavailableReason. The backend's unavailableMessage is written for admins (and can carry
// ids), so it is never shown. Codes also arrive here from a refused POST /pay/bank.
function unavailableCopy(reason: string | null, currency: string): { title: string; body: string } {
  switch (reason) {
    case "BankPayment.PayByBankNotEnabled":
      return {
        title: "Pay by bank is switched off",
        body: "Paying bills by bank from this link has been switched off. Please pay this bill another way.",
      };
    case "BankPayment.NotConfigured":
      return {
        title: "Pay by bank isn't set up yet",
        body: "Bank payments aren't set up for your account yet, so this bill can't be paid from here. Please try again later or pay it another way.",
      };
    case "Bill.TransitionFailed":
      return {
        title: "This bill can't be paid right now",
        body: "The bill has changed since this link was sent. Check it in your account before paying.",
      };
    case "BankPayment.UnsupportedCurrency":
      return {
        title: "This currency can't be paid by bank",
        body: `Pay by bank only supports bills in pounds sterling (GBP). Please pay this ${currency} bill another way.`,
      };
    case "BankPayment.NothingToPay":
      return {
        title: "Nothing left to pay",
        body: "This bill has no outstanding balance, so there's nothing to send.",
      };
    case "BankPayment.MissingSupplier":
      return {
        title: "No supplier on this bill",
        body: "This bill isn't linked to a supplier yet, so there's no one to pay. Ask an admin to add the supplier, then come back to this link.",
      };
    case "BankPayment.SupplierBankDetailsNotVerified":
      return {
        title: "Supplier bank details need confirming",
        body: "The supplier's bank details haven't been confirmed yet. Ask an admin to confirm them, then come back to this link.",
      };
    case "BankPayment.MissingBankDetails":
      return {
        title: "Supplier bank details are missing",
        body: "We don't have complete, valid bank details for this supplier. Ask an admin to add their sort code and account number, then come back to this link.",
      };
    case "BankPayment.NotRetryable":
      return {
        title: "A payment is already on its way",
        body: "An earlier payment for this bill was approved at the bank and may still go through. Wait for it to finish before trying again.",
      };
    default:
      return {
        title: "Pay by bank isn't available right now",
        body: "This bill can't be paid by bank at the moment. Please try again later or pay it another way.",
      };
  }
}

// Codes a refused POST can carry that mean "this bill can't be paid now", not "try another bank".
const UNAVAILABLE_CODES = new Set([
  "BankPayment.PayByBankNotEnabled",
  "BankPayment.NotConfigured",
  "Bill.TransitionFailed",
  "BankPayment.UnsupportedCurrency",
  "BankPayment.NothingToPay",
  "BankPayment.MissingSupplier",
  "BankPayment.SupplierBankDetailsNotVerified",
  "BankPayment.MissingBankDetails",
  "BankPayment.NotRetryable",
]);

function MessageCard({
  brand,
  icon,
  title,
  description,
  children,
}: {
  brand?: string;
  icon: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  const body = (
    <>
      {/* CardHeader is a grid, so items-center alone leaves the icon at the start: justify-items centres it. */}
      <CardHeader className="items-center justify-items-center gap-3 text-center">
        {icon}
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="flex justify-center text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </>
  );
  return brand ? <BrandedCard brand={brand}>{body}</BrandedCard> : <Card>{body}</Card>;
}

function BillReference({ bill }: { bill: Bill }) {
  const parts = [bill.supplierName, bill.invoiceNumber ? `Invoice ${bill.invoiceNumber}` : null].filter(Boolean);
  return parts.length ? <div>{parts.join(" · ")}</div> : null;
}

// `compact` drops the fact rows so the bank list and progress steps sit near the top on a phone.
function BillSummaryCard({
  bill,
  brand,
  compact,
  children,
}: {
  bill: Bill;
  brand: string;
  compact?: boolean;
  children: ReactNode;
}) {
  const dueDate = formatDate(bill.dueDate);
  const partlySettled = bill.outstanding < bill.totalAmount;
  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        {bill.clientLogoUrl ? (
          <img src={bill.clientLogoUrl} alt="" className="h-10 w-auto max-w-[160px] object-contain" />
        ) : null}
        <CardDescription>{bill.organisationName}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <div
            className={cn("font-semibold tracking-tight", compact ? "text-3xl" : "text-4xl sm:text-5xl")}
            style={isDarkEnoughForText(brand) ? { color: brand } : undefined}
          >
            {formatCurrency(bill.outstanding, bill.currency)}
          </div>
          {bill.supplierName ? (
            <div className="mt-1 text-sm text-muted-foreground">to {bill.supplierName}</div>
          ) : null}
        </div>

        {compact ? null : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {bill.invoiceNumber ? <Fact label="Invoice number" value={bill.invoiceNumber} /> : null}
            {dueDate ? <Fact label="Due" value={dueDate} /> : null}
            {partlySettled ? (
              <Fact label="Bill total" value={formatCurrency(bill.totalAmount, bill.currency)} />
            ) : null}
          </dl>
        )}

        <Payee bill={bill} />

        {children}
      </CardContent>
    </BrandedCard>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </>
  );
}

// Who the money goes to. Last four digits only: enough to recognise the account, not to reuse it.
function Payee({ bill }: { bill: Bill }) {
  if (!bill.payeeName && !bill.payeeAccountNumberLast4) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm">
      <Landmark className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <span className="text-muted-foreground">Paying to </span>
        <span className="font-medium">{bill.payeeName ?? "the supplier's account"}</span>
        {bill.payeeAccountNumberLast4 ? (
          <span className="ml-1.5 font-mono text-muted-foreground tabular-nums">
            ••••{bill.payeeAccountNumberLast4}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Notice({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div className="space-y-1 text-sm">
          <p className="font-medium">{title}</p>
          <div className="text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  );
}

function PayByBankButton({ brand, label, onClick }: { brand: string; label: string; onClick: () => void }) {
  return (
    <div className="space-y-2">
      <Button
        size="lg"
        className="w-full"
        style={{ backgroundColor: brand, color: pickForeground(brand) }}
        onClick={onClick}
      >
        <Landmark className="size-4" />
        {label}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        You'll choose your bank and approve the payment in your banking app.
      </p>
    </div>
  );
}

/** Mandatory Yapily Connect disclosure, rendered VERBATIM (regulatory). Mirrors sandbox's DisclosureNotice in PayByBank.tsx. */
function DisclosureNotice({ disclosure }: { disclosure: YapilyDisclosure }) {
  return (
    <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
      <p>{disclosure.text}</p>
      <p className="mt-1.5 space-x-3">
        <a href={disclosure.termsUrl} target="_blank" rel="noreferrer" className="underline hover:text-foreground">
          Terms &amp; Conditions
        </a>
        <a href={disclosure.privacyUrl} target="_blank" rel="noreferrer" className="underline hover:text-foreground">
          Privacy Notice
        </a>
      </p>
    </div>
  );
}

type BankList =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; institutions: PaymentInstitution[] };

// Bank selection + consent. On confirm the backend starts the payment and the browser goes to the payer's own
// bank; the bank's callback brings them back to /p/{token}?billPaymentId=...&status=...
function BankPicker({
  token,
  bill,
  brand,
  mock,
  onBack,
  onRedirecting,
  onUnavailable,
  onInvalid,
}: {
  token: string;
  bill: Bill;
  brand: string;
  mock: boolean;
  onBack: () => void;
  onRedirecting: () => void;
  onUnavailable: (reason: string) => void;
  onInvalid: () => void;
}) {
  const [banks, setBanks] = useState<BankList>({ kind: "loading" });
  const [loadKey, setLoadKey] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bankQuery, setBankQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (mock) {
      setBanks({ kind: "loaded", institutions: mockBillPaymentInstitutions.institutions });
      return;
    }
    let cancelled = false;
    setBanks({ kind: "loading" });
    fetchBillPaymentInstitutions(token).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setBanks({ kind: "loaded", institutions: result.data.institutions });
        if (result.data.institutions.length === 1) setSelectedId(result.data.institutions[0]!.id);
        return;
      }
      const err = result.error;
      if (err.kind === "invalid-token") return onInvalid();
      const code = err.kind === "unknown" ? (err.problem.code ?? err.problem.title) : undefined;
      if (code === "BankPayment.NotConfigured") return onUnavailable(code);
      setBanks({
        kind: "error",
        message:
          err.kind === "network"
            ? "Couldn't load the list of banks. Check your connection and try again."
            : "Couldn't load the list of banks. Please try again.",
      });
    });
    return () => {
      cancelled = true;
    };
    // onInvalid/onUnavailable are left out on purpose: they are fresh closures each render.
  }, [token, mock, loadKey]);

  const institutions = banks.kind === "loaded" ? banks.institutions : [];
  // Client-side over a list already in memory, so no debounce: there is no request to save.
  const query = bankQuery.trim().toLowerCase();
  const visible = query ? institutions.filter((inst) => inst.name.toLowerCase().includes(query)) : institutions;
  // A bank the search has hidden is not the payer's choice any more. The pick comes back if the search is cleared.
  const chosenId = visible.some((inst) => inst.id === selectedId) ? selectedId : null;

  async function handleContinue() {
    if (!chosenId || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    if (mock) {
      onRedirecting();
      return;
    }
    const result = await payBillByBank(token, chosenId);
    if (result.ok) {
      onRedirecting();
      // Off to the payer's own bank. No reset: we're navigating away.
      window.location.assign(result.data.authorisationUrl);
      return;
    }
    const err = result.error;
    if (err.kind === "invalid-token") return onInvalid();
    const code = err.kind === "unknown" ? (err.problem.code ?? err.problem.title) : undefined;
    if (code && UNAVAILABLE_CODES.has(code)) return onUnavailable(code);
    // A 400 ValidationProblem means the supplier's stored bank details fail validation.
    if (err.kind === "unknown" && err.status === 400 && err.problem.errors)
      return onUnavailable("BankPayment.MissingBankDetails");
    console.error("Pay by bank error", { error: err });
    setSubmitError(
      err.kind === "network"
        ? "Couldn't reach the payment service. Check your connection and try again."
        : code === "BankPayment.ProviderError"
          ? "We couldn't reach your bank. Please try again, or choose a different bank."
          : "We couldn't start the payment. Please try again.",
    );
    setSubmitting(false);
  }

  const searchable = institutions.length > BANK_SEARCH_THRESHOLD;

  return (
    <div className="space-y-4">
      {banks.kind === "loading" ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          Loading banks...
        </div>
      ) : banks.kind === "error" ? (
        <div className="space-y-3 py-4 text-center">
          <p className="text-sm text-destructive">{banks.message}</p>
          <Button variant="outline" onClick={() => setLoadKey((k) => k + 1)}>
            Try again
          </Button>
        </div>
      ) : institutions.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No banks are available right now. Please try again later.
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor={searchable ? "bank-search" : undefined}>Choose your bank</Label>
          {searchable ? (
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="bank-search"
                value={bankQuery}
                onChange={(e) => setBankQuery(e.target.value)}
                placeholder="Search banks"
                className="h-10 pl-9"
              />
            </div>
          ) : null}
          <div className="max-h-72 space-y-1.5 overflow-auto pr-1">
            {visible.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No bank matches that name.</p>
            ) : null}
            {visible.map((inst) => {
              const selected = chosenId === inst.id;
              return (
                <button
                  key={inst.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setSelectedId(inst.id)}
                  className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  style={selected ? { borderColor: brand, backgroundColor: tintFor(brand, 0.08) } : undefined}
                >
                  {inst.iconUrl ? <img src={inst.iconUrl} alt="" className="size-6 shrink-0 object-contain" /> : null}
                  <span className="text-sm font-medium">{inst.name}</span>
                  {selected ? (
                    <CheckCircle2
                      className="ml-auto size-4 shrink-0"
                      style={isDarkEnoughForText(brand) ? { color: brand } : undefined}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {bill.isDemo ? (
        // Nothing reaches Yapily in a demo, so its disclosure would be untrue here.
        <p className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">Demo: no real bank is contacted.</p>
      ) : (
        <DisclosureNotice disclosure={bill.disclosure} />
      )}

      {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

      <div className="flex gap-2">
        <Button variant="ghost" disabled={submitting} onClick={onBack}>
          Back
        </Button>
        <Button
          className="flex-1"
          disabled={!chosenId || submitting}
          onClick={handleContinue}
          style={{ backgroundColor: brand, color: pickForeground(brand) }}
        >
          {submitting ? <Spinner /> : <ExternalLink className="size-4" />}
          Continue to your bank
        </Button>
      </div>
    </div>
  );
}

function InProgress({
  bill,
  timedOut,
  onCheckAgain,
}: {
  bill: Bill;
  timedOut: boolean;
  onCheckAgain: () => void;
}) {
  // Pending: the bank holds the payment. On a business account that usually means a second signatory must approve.
  const awaitingApprover = bill.latestPayment?.status === "Pending";
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        {timedOut ? <AlertTriangle className="size-6 text-amber-500" /> : <Spinner className="size-6" />}
        <div className="space-y-1">
          <p className="font-medium">
            {awaitingApprover ? "Waiting at your bank" : timedOut ? "Still processing" : "Confirming your payment"}
          </p>
          <p className="text-sm text-muted-foreground">
            {awaitingApprover
              ? "Your bank has the payment and is waiting for it to be approved."
              : timedOut
                ? "Your bank hasn't confirmed the payment yet."
                : "We're checking with your bank. This usually takes a few seconds."}
          </p>
        </div>
      </div>
      {awaitingApprover ? (
        <Notice icon={<Users className="h-5 w-5 text-muted-foreground" />} title="Another approver may be needed">
          Some business accounts need a second person to approve payments. If yours does, ask them to approve it in
          their banking app.
        </Notice>
      ) : null}
      {timedOut ? (
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">
            You can close this page. The bill will be marked as paid once the payment goes through.
          </p>
          <Button variant="outline" onClick={onCheckAgain}>
            Check again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
